import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button, FormField, Input, Modal, toast } from '@/ui';
import { MoneyText } from '@/ui';
import { totalAmount } from '@/lib/money';
import { isAmountInRange, parseAmount } from '@/lib/task-amount';
import { findAccountsForCustomer, type BankAccount } from '@/lib/task-accounts';
import { pairOf } from '@/lib/task-currency';
import { useSessionStore } from '@/auth/session.store';
import { useCreateTask, useTaskAccounts } from './queries';
import { taskErrorMessage } from './error-message';
import styles from './CreateTaskModal.module.css';

/**
 * TALEP OLUSTURMA.
 *
 * Kur kartindan geliyor: kullanici hangi para birimini hangi YONDE istedigini
 * zaten secti; burada yalniz BANKA ve TUTAR kaliyor.
 *
 * Onay adimi (ikinci ekran) bilincli bir SURTUNME: talep acmak bir taahhut ve
 * geri sayim aninda basliyor. Ozet, islemi ve tutari birlikte gosteriyor.
 *
 * BANKA SECIMI bir liste degil, UYGUNLUK KAPISI: takas iki bacakli ve ikisi de
 * ayni kurumdan yuruyor. Uygun hesap yoksa talep ACILAMAZ ve sebebi soylenir —
 * calismayacak bir formu doldurtmak, en kotu turden bir hata mesajidir.
 */

export type CreateTaskModalProps = {
  open: boolean;
  onClose: () => void;
  /** Kur satirindan gelen gorev tipi ('USD', 'EUR/USD'). */
  taskTypeCode: string;
  currencyName: string;
  isBuying: boolean;
};

export function CreateTaskModal(props: CreateTaskModalProps) {
  // Modal kapaliyken govde CALISMAZ: taslak state her acilista temiz baslar ve
  // "acilista formu sifirla" diye bir efekte gerek kalmaz.
  if (!props.open) return null;
  return <CreateTaskForm {...props} />;
}

function CreateTaskForm({ onClose, taskTypeCode, currencyName, isBuying }: CreateTaskModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const snapshot = useSessionStore((state) => state.snapshot);
  const userId = snapshot?.user.id;

  const accountsQuery = useTaskAccounts(userId);
  // Hesap EKLEYEBILIR mi: organizasyon uyesi ekleyemez, org'suz kullanici kendi hesabinin
  // sahibidir (AccountsPage'deki `canManage` ile ayni ayrim).
  const inOrganization = Boolean(snapshot?.organization);
  const eligible = findAccountsForCustomer(accountsQuery.data ?? [], taskTypeCode, isBuying);

  const [selected, setSelected] = useState<BankAccount | null>(eligible[0] ?? null);
  const [amountText, setAmountText] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [confirming, setConfirming] = useState(false);

  const createTask = useCreateTask();
  const pair = pairOf(taskTypeCode);
  const direction = t(isBuying ? 'labels.buy' : 'labels.sell');
  const account = selected ?? eligible[0] ?? null;

  const parsed = parseAmount(amountText);

  const review = (): void => {
    if (parsed === null) {
      setError(t('offerFlow.amountInvalid'));
      return;
    }
    if (!isAmountInRange(parsed)) {
      setError(t('offerFlow.amountTooLarge'));
      return;
    }
    if (!account) {
      setError(t('task.accountMismatch'));
      return;
    }
    setError(undefined);
    setConfirming(true);
  };

  const submit = (): void => {
    if (parsed === null || !account || !userId) return;
    createTask.mutate(
      {
        taskUserId: userId,
        taskType: taskTypeCode,
        taskAmount: parsed,
        taskIsBuying: isBuying,
        taskData: { selectedAccount: account },
      },
      {
        onSuccess: () => {
          toast({ message: t('task.created'), tone: 'success' });
          onClose();
          // Talep acildi; kullanici onu izleyecegi yere goturuluyor. Liste zaten
          // gecersiz kilindi (bkz. createTaskMutationOptions), yani yeni kayit
          // orada gorunur.
          void navigate('/musteri/talepler');
        },
        onError: (error) => {
          setConfirming(false);
          // ACCOUNT_LEG_MISMATCH / ACCOUNT_COUNTER_LEG_MISSING (2026-09-23) sunucuda da var;
          // eslenmeyen kod eski genel metne duser.
          setError(taskErrorMessage(error, t, 'offerFlow.createFailed'));
        },
      },
    );
  };

  if (confirming && parsed !== null) {
    return (
      <Modal
        open
        onClose={() => {
          setConfirming(false);
        }}
        title={t('task.confirmTitle')}
        width="sm"
        dismissOnScrim={!createTask.isPending}
        footer={
          <>
            <Button
              tone="ghost"
              disabled={createTask.isPending}
              onClick={() => {
                setConfirming(false);
              }}
            >
              {t('actions.cancel')}
            </Button>
            <Button loading={createTask.isPending} onClick={submit}>
              {t('actions.confirm')}
            </Button>
          </>
        }
      >
        <dl className={styles.summary}>
          <div className={styles.summaryRow}>
            <dt>{t('labels.transaction')}</dt>
            <dd>{`${pair.code} ${direction}`}</dd>
          </div>
          <div className={styles.summaryRow}>
            <dt>{t('labels.amount')}</dt>
            <dd>
              <MoneyText total={totalAmount(parsed)} currency={pair.currency} size="h3" tone="primary" />
            </dd>
          </div>
          <div className={styles.summaryRow}>
            <dt>{t('task.selectBank')}</dt>
            <dd>{account?.accountLabel ?? account?.accountCode ?? ''}</dd>
          </div>
        </dl>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} title={`${currencyName} ${direction}`} width="sm">
      {accountsQuery.isPending ? (
        <p className={styles.hint}>{t('common.loading')}</p>
      ) : eligible.length === 0 ? (
        /* IKI AYRI DURUM, IKI AYRI CUMLE.
           Organizasyon uyesi hesap EKLEYEMEZ (IBAN'lar 2026-08-30'dan beri platform
           yonetiminde); ona "hesap ekleyiniz" demek imkansiz bir isle gondermek olurdu —
           accounts.readOnly metnindeki ayni hata. Organizasyonsuz kullanici kendi hesabinin
           sahibi oldugu icin eski cumle onda dogru kalir. */
        <p className={styles.warning}>
          {inOrganization ? t('task.accountMissingPlatform') : t('task.accountMismatch')}
        </p>
      ) : (
        <div className={styles.body}>
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>{t('task.selectBank')}</legend>
            <div className={styles.accounts}>
              {eligible.map((option) => {
                const active = option.accountCode === account?.accountCode;
                return (
                  <button
                    key={option.accountCode}
                    type="button"
                    aria-pressed={active}
                    className={[styles.account, active ? styles.accountSelected : undefined]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => {
                      setSelected(option);
                    }}
                  >
                    {option.accountLabel ?? option.accountCode}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <FormField label={`${t('labels.amount')} ${pair.currency}`} error={error}>
            <Input
              numeric
              autoFocus
              inputMode="decimal"
              value={amountText}
              onChange={(event) => {
                setAmountText(event.target.value);
              }}
            />
          </FormField>

          <div className={styles.actions}>
            <Button tone="ghost" onClick={onClose}>
              {t('actions.cancel')}
            </Button>
            <Button onClick={review}>{t('actions.confirm')}</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
